/**
 * Events Service
 */

import { supabase } from "./supabase";
import { toRpcError } from "../utils/rpcError";
import {
  SCHEDULE_FIELDS,
  copyStaffingToDuplicates,
  describeDateModelError,
  isDateModelActive,
  normalizeScheduleForWriter,
  sameSchedule,
  setEventDates,
  type EventSchedule,
  type StaffingCopyFailure,
} from "./eventDateModel.service";
import type { UUID } from "../types/common.types";
import type { Event } from "../types/event.types";
import {
  eventTypeHasNoCtaQr,
  eventTypeIsNoPromo,
} from "../utils/eventTypePolicy";
import {
  canonicalAttendance,
  isMemberForHeadcount,
  type MemberEvidenceLike,
} from "../utils/eventAttendance";

// Query parameters for fetching events.
// R4 (2026-05-17): all filters server-side per audit verdict.
// Pagination total reflects the FILTERED count so the operator
// always sees an honest "X of Y" — and exports of "filtered data"
// actually cover all matching rows, not just the loaded page.
export interface GetEventsParams {
  status?: string;
  // Statuses to EXCLUDE (e.g. ["completed","cancelled"] for the default
  // "active events" list view). Mutually exclusive with `status` in practice —
  // when a specific status is chosen the caller passes `status` instead. Applied
  // as `status <> ALL(...)` on the non-org path and forwarded to
  // get_events_for_organizations (p_exclude_statuses) on the org path.
  excludeStatuses?: string[];
  // Filter by the "setup incomplete" flag: true = only incomplete, false = only
  // complete, undefined = no filter.
  setupIncomplete?: boolean;
  // mig 1279 (owner 2026-10-30): Gun Show - Not Advertised filter. true = only not-advertised gun shows, false = only
  // the others, undefined = no filter.
  notAdvertised?: boolean;
  type?: string;
  dateFrom?: string;
  dateTo?: string;
  state?: string;
  organization_id?: string;
  search?: string;
  // OI-10 (2026-06-20): server-side Lead/Event Rep + TM/RM/SD position filters,
  // matching the four events hierarchy columns. Applied as plain .eq() on the
  // non-org path and forwarded to get_events_for_organizations on the org path
  // so the two combine server-side (honest count + export).
  sales_position_id?: string;
  territory_manager_position_id?: string;
  regional_manager_position_id?: string;
  sales_director_position_id?: string;
  page: number;
  limit: number;
  sortField?: string;
  sortDirection?: "asc" | "desc";
}

// Create/Update event input - partial Event without auto-generated fields
export type CreateEventInput = Omit<Event, "id" | "created_at" | "updated_at">;
export type UpdateEventInput = Partial<CreateEventInput>;

/** mig 1279: which of these events are a Gun Show - Not Advertised (chunked; RLS applies). */
const fetchNotAdvertisedIds = async (ids: string[]): Promise<Set<string>> => {
  const out = new Set<string>();
  for (let i = 0; i < ids.length; i += 200) {
    const chunk = ids.slice(i, i + 200);
    if (chunk.length === 0) {
      continue;
    }
    const { data, error } = await supabase
      .from("events")
      .select("id")
      .in("id", chunk)
      .eq("not_advertised", true);
    if (error) {
      throw error;
    }
    for (const r of data ?? []) {
      out.add((r as { id: string }).id);
    }
  }
  return out;
};

export const getEvents = async (params?: GetEventsParams) => {
  // R5 (2026-05-17): when filtering by organization_id we route through
  // the new get_events_for_organizations RPC which joins server-side and
  // bypasses the prior unbounded event_organizations prefetch
  // (R3+R4 post-ship audit finding #2).
  // ⭐ 2026-09-25: date-filtered queries MUST go through the RPC. PostgREST cannot express
  // `AT TIME ZONE`, and the day filter has to be evaluated in each event's OWN timezone to agree
  // with the calendar grid — 34% of events end on a different date in UTC than locally, and 70%
  // are multi-day. The RPC accepts p_org_ids = NULL for the unscoped case.
  if (params?.organization_id || params?.dateFrom || params?.dateTo) {
    // The list RPC has no not_advertised column / parameter: with that filter on, fetch the matching gun shows (up to
    // 5,000 -- not-advertised shows are few), keep the flagged ones and page on the client.
    const naFilter = params.notAdvertised;
    const rpcType = naFilter === true ? "gun_show" : params.type || null;
    const { data, error } = await supabase.rpc("get_events_for_organizations", {
      p_org_ids: params.organization_id ? [params.organization_id] : null,
      p_status: params.status || null,
      p_exclude_statuses:
        params.excludeStatuses && params.excludeStatuses.length > 0
          ? params.excludeStatuses
          : null,
      p_setup_incomplete: params.setupIncomplete ?? null,
      p_type: rpcType,
      p_state: params.state || null,
      p_date_from: params.dateFrom || null,
      p_date_to: params.dateTo || null,
      p_search: params.search || null,
      p_sort:
        params.sortField === "start_time" ||
        params.sortField === "created_at" ||
        params.sortField === "title" ||
        params.sortField === "status" ||
        params.sortField === "type" ||
        params.sortField === "setup_incomplete"
          ? params.sortField
          : "start_time",
      p_sort_direction: params.sortDirection === "asc" ? "asc" : "desc",
      p_page: naFilter === undefined ? params.page : 1,
      p_limit: naFilter === undefined ? params.limit : 5000,
      // OI-10: forward the position filters so org + rep/manager combine server-side.
      p_sales_position_id: params.sales_position_id || null,
      p_tm_position_id: params.territory_manager_position_id || null,
      p_rm_position_id: params.regional_manager_position_id || null,
      p_sd_position_id: params.sales_director_position_id || null,
    });
    if (error) {
      throw error;
    }
    const rpcRows = (data ?? []) as Array<{
      id: string;
      total_count: number;
      [k: string]: unknown;
    }>;
    const total = rpcRows.length > 0 ? Number(rpcRows[0].total_count) : 0;
    // RPC return is already shaped like events rows + cta_count; comment
    // count isn't part of the RPC (no consumer of comment_count in the
    // org-filtered path today), default to 0.
    // comment_count now comes from the RPC (migration 2026-09-25-024). It used to be hardcoded
    // to 0 here, which silently emptied the badge on every organization-filtered view.
    let events = rpcRows.map((r) => ({
      ...r,
      comment_count: Number(r.comment_count ?? 0),
      not_advertised: false,
    }));
    // the not_advertised flag of the rows on this page (badge), read through RLS like the list itself
    const flagged = await fetchNotAdvertisedIds(events.map((e) => e.id));
    events = events.map((e) => ({ ...e, not_advertised: flagged.has(e.id) }));
    if (naFilter !== undefined) {
      const kept = events.filter((e) => e.not_advertised === naFilter);
      const start = (params.page - 1) * params.limit;
      return {
        events: kept.slice(start, start + params.limit),
        total: kept.length,
      };
    }
    return { events, total };
  }

  let query = supabase.from("events").select(
    `
    *,
    event_call_to_actions(id, is_active),
    event_comments(id)
  `,
    { count: "exact" },
  );

  // Exclude soft-deleted events
  query = query.or("is_deleted.is.null,is_deleted.eq.false");

  if (params?.status) {
    query = query.eq("status", params.status);
  }
  // Default "active events" view: hide completed/cancelled unless the operator
  // explicitly filters to a specific status or "all".
  if (params?.excludeStatuses && params.excludeStatuses.length > 0) {
    query = query.not("status", "in", `(${params.excludeStatuses.join(",")})`);
  }
  if (params?.notAdvertised !== undefined) {
    query = query.eq("not_advertised", params.notAdvertised);
  }
  if (params?.setupIncomplete !== undefined) {
    query = query.eq("setup_incomplete", params.setupIncomplete);
  }
  if (params?.type) {
    query = query.eq("type", params.type);
  }
  if (params?.state) {
    // R4 audit Decision 2: filter on the indexed column; display
    // falls back to location_address.state for legacy rows that
    // never populated this column.
    query = query.eq("location_state", params.state);
  }
  // OI-10 (2026-06-20): rep/manager position filters on the non-org path.
  if (params?.sales_position_id) {
    query = query.eq("sales_position_id", params.sales_position_id);
  }
  if (params?.territory_manager_position_id) {
    query = query.eq(
      "territory_manager_position_id",
      params.territory_manager_position_id,
    );
  }
  if (params?.regional_manager_position_id) {
    query = query.eq(
      "regional_manager_position_id",
      params.regional_manager_position_id,
    );
  }
  if (params?.sales_director_position_id) {
    query = query.eq(
      "sales_director_position_id",
      params.sales_director_position_id,
    );
  }
  // NOTE: there is deliberately NO date filtering here. It lived in this path AND in
  // get_events_for_organizations with the same defect in both, which is how "events on Saturday"
  // meant "events STARTING on Saturday" for long enough to be reported from production. Any query
  // carrying dateFrom/dateTo is routed to the RPC above, which filters in the event's own timezone.
  // If you add a date filter here, you are re-creating the second copy.

  if (params?.search && params.search.trim() !== "") {
    // Bounded search per audit must-fix: title + location only.
    // Avoids unindexed cross-table OR on description/org-name.
    // Sanitize PostgREST grammar chars in .or() — R3+R4 post-ship audit
    // finding #1. Same sanitization pattern used in EventsOrgFilter +
    // EventOrganizationLocationSelector after the R1+R2 post-ship audit.
    const t = params.search.trim().replace(/[,()."*\\]/g, "");
    if (t) {
      query = query.or(
        `title.ilike.*${t}*,location_name.ilike.*${t}*,location_city.ilike.*${t}*`,
      );
    }
  }
  // Apply pagination if params provided, otherwise get all events
  if (params?.page && params?.limit) {
    query = query.range(
      (params.page - 1) * params.limit,
      params.page * params.limit - 1,
    );
  }

  const sortField = params?.sortField || "start_time";
  const ascending = params?.sortDirection === "asc" ? true : false;
  const { data, error, count } = await query.order(sortField, {
    ascending,
  });

  if (error) {
    throw error;
  }

  // Transform data to include cta_count and comment_count
  const eventsWithCounts = (data || []).map((event) => ({
    ...event,
    // Switched-off CTAs (migration 2026-10-29-1260) are not counted.
    cta_count:
      (
        event.event_call_to_actions as Array<{ is_active?: boolean }> | null
      )?.filter((c) => c.is_active !== false).length || 0,
    comment_count: event.event_comments?.length || 0,
    event_call_to_actions: undefined, // Remove the raw join data
    event_comments: undefined, // Remove the raw join data
  }));

  // Owner 2026-10-29: a class, table, seminar or speaking engagement shows its venue's / host organization's CTAs
  // instead of its own (switched off), so its badge counts those (migration 1273; the RPC path above already does).
  const noPromoIds = eventsWithCounts
    .filter((e) => eventTypeIsNoPromo((e as { type?: string | null }).type))
    .map((e) => e.id as string);
  if (noPromoIds.length > 0) {
    const { data: hostCounts, error: hostError } = await supabase.rpc(
      "get_event_host_cta_counts",
      { p_event_ids: noPromoIds },
    );
    if (hostError) {
      console.warn("Host CTA counts unavailable:", hostError.message);
    } else {
      const byId = new Map(
        (
          (hostCounts ?? []) as Array<{
            event_id: string;
            host_cta_count: number;
          }>
        ).map((r) => [r.event_id, Number(r.host_cta_count) || 0]),
      );
      for (const e of eventsWithCounts) {
        if (byId.has(e.id as string)) {
          e.cta_count = byId.get(e.id as string)!;
        }
      }
    }
  }

  return { events: eventsWithCounts, total: count || 0 };
};

export const getEventById = async (id: UUID) => {
  const { data, error } = await supabase
    .from("events")
    .select("*")
    .eq("id", id)
    .single();
  if (error) {
    throw error;
  }
  return data;
};

// Accepts an OPTIONAL caller-supplied primary key so a create can be made
// idempotent across an ambiguous failure (DB commit whose response was lost).
// The wizard generates a stable client-side UUID and reuses it on retry; if the
// first attempt actually committed, the retry's insert collides on the PK and we
// recover the already-created row instead of surfacing a spurious duplicate.
export const createEvent = async (
  event: CreateEventInput & { id?: string },
) => {
  const { data, error } = await supabase
    .from("events")
    .insert([event])
    .select()
    .single();
  if (error) {
    // Ambiguous-retry recovery: unique_violation (23505) on the supplied id means
    // a prior attempt already created THIS row. Return it rather than erroring.
    if (event.id && (error.code === "23505" || error.code === "409")) {
      const { data: existing } = await supabase
        .from("events")
        .select()
        .eq("id", event.id)
        .maybeSingle();
      if (existing) {
        return existing;
      }
    }
    throw error;
  }
  return data;
};

/**
 * A date-model event whose new dates were committed by set_event_dates, but whose other fields then failed to
 * save. The event is NOT unchanged: callers must refresh it (useUpdateEvent does) and show this message.
 */
export class EventPartialSaveError extends Error {
  readonly partialSave = true;
  constructor(cause: unknown) {
    const why =
      (cause as { message?: string } | null)?.message ?? "unknown error";
    super(`Dates were saved, but other changes were not: ${why}`);
    this.name = "EventPartialSaveError";
    (this as { cause?: unknown }).cause = cause;
  }
}

export const isEventPartialSaveError = (
  e: unknown,
): e is EventPartialSaveError =>
  (e as { partialSave?: unknown } | null)?.partialSave === true;

export const updateEvent = async (id: UUID, updates: UpdateEventInput) => {
  console.log("[Events Service] Updating event", { id, updates });
  const scheduleKeys = SCHEDULE_FIELDS.filter(
    (k) => k in (updates as Record<string, unknown>),
  );
  let datesWritten = false;
  if (scheduleKeys.length > 0 && (await isDateModelActive(id))) {
    // Date-model event (MSR core): its dates change only through set_event_dates, which keeps the event's days
    // consistent. The forms resend dates on every save, so call the writer only when a date really changed, and
    // save every other field with the ordinary update.
    const { data: cur, error: curErr } = await supabase
      .from("events")
      .select("start_time, end_time, timezone, date_tbd")
      .eq("id", id)
      .single();
    if (curErr) {
      throw curErr;
    }
    const u = updates as Record<string, unknown>;
    const current = cur as EventSchedule;
    // A complete, writer-ready schedule (all five arguments; TBD in its canonical shape).
    const next = normalizeScheduleForWriter(u, current);
    if (!sameSchedule(next, current)) {
      try {
        await setEventDates(id, next);
      } catch (e) {
        const friendly = describeDateModelError(e);
        throw friendly ? Object.assign(new Error(friendly), { cause: e }) : e;
      }
      datesWritten = true;
    }
    const rest = Object.fromEntries(
      Object.entries(u).filter(
        ([k]) => !(SCHEDULE_FIELDS as readonly string[]).includes(k),
      ),
    );
    if (Object.keys(rest).length === 0) {
      const { data: fresh, error: freshErr } = await supabase
        .from("events")
        .select()
        .eq("id", id)
        .single();
      if (freshErr) {
        // The dates (if any) are saved; only the re-read failed.
        throw datesWritten ? new EventPartialSaveError(freshErr) : freshErr;
      }
      return fresh;
    }
    updates = rest as UpdateEventInput;
  }
  const { data, error } = await supabase
    .from("events")
    .update(updates)
    .eq("id", id)
    .select()
    .single();
  if (error) {
    console.error("[Events Service] Update failed:", error);
    // set_event_dates already committed: say so, so the caller refreshes instead of showing stale dates.
    throw datesWritten ? new EventPartialSaveError(error) : error;
  }
  console.log("[Events Service] Update successful:", data);
  return data;
};

export const cancelEvent = async (
  id: UUID,
  cancellation_reason?: string,
  cancellation_notes?: string,
) => {
  const updates: Record<string, unknown> = { status: "cancelled" };
  if (cancellation_reason) {
    updates.cancellation_reason = cancellation_reason;
  }
  if (cancellation_notes) {
    updates.cancellation_notes = cancellation_notes;
  }
  const { data, error } = await supabase
    .from("events")
    .update(updates)
    .eq("id", id)
    .select()
    .single();
  if (error) {
    throw error;
  }
  return data;
};

/**
 * Regional / Territory Managers (owner 2026-11-04, migrations 1701 / 1702). Their direct events UPDATE stays refused by
 * RLS (they change no other event field), so the three things they may do on an event they can see go through RPCs:
 * the date / time (fm_set_event_schedule: a date-model event through set_event_dates, so its days stay consistent),
 * Cancel (fm_cancel_event) and Archive = hide from the list (fm_archive_event, the same soft delete as Delete). Each RPC
 * refuses any other role (FIELD_MANAGER_ONLY): everyone else keeps updateEvent / cancelEvent / softDeleteEvent.
 */
export const fmSetEventSchedule = async (
  id: UUID,
  s: {
    start_time: string;
    end_time: string | null;
    timezone: string;
    all_day: boolean;
  },
) => {
  const { data, error } = await supabase.rpc("fm_set_event_schedule", {
    p_event_id: id,
    p_start_time: s.start_time,
    p_end_time: s.end_time ?? null,
    p_timezone: s.timezone,
    p_date_tbd: false,
    p_all_day: s.all_day,
  });
  if (error) {
    throw error;
  }
  return data;
};

export const fmCancelEvent = async (
  id: UUID,
  cancellation_reason?: string,
  cancellation_notes?: string,
) => {
  const { data, error } = await supabase.rpc("fm_cancel_event", {
    p_event_id: id,
    p_reason: cancellation_reason || null,
    p_notes: cancellation_notes || null,
  });
  if (error) {
    throw error;
  }
  return data;
};

export const fmArchiveEvent = async (id: UUID) => {
  const { data, error } = await supabase.rpc("fm_archive_event", {
    p_event_id: id,
  });
  if (error) {
    throw error;
  }
  return data;
};

/**
 * The sentence for a refusal of the three RM / TM event actions (or null to keep the caller's generic message).
 */
export function describeFieldManagerEventError(e: unknown): string | null {
  const msg = (e as { message?: string } | null)?.message ?? "";
  if (/EVENT_DAY_OPEN/.test(msg)) {
    return "A day of this event was started and is not closed yet. The lead closes the day first (or ask an admin).";
  }
  if (/FIELD_MANAGER_OUT_OF_SCOPE/.test(msg)) {
    return "This event is outside your team, so you can't change it.";
  }
  if (/FIELD_MANAGER_EVENT_NOT_ACTIVE/.test(msg)) {
    return "This event is cancelled, completed or deleted, so you can't change it. Ask an admin.";
  }
  if (/FIELD_MANAGER_CANCEL_COMPLETED/.test(msg)) {
    return "A completed event can't be cancelled here. Ask an admin.";
  }
  if (/EVENT_HAS_ACTIVITY/.test(msg)) {
    return "This event has check-ins, tickets or vouchers, so it can't be archived. Cancel it instead.";
  }
  if (/MSR_DAY_HAS_RECORD|ACTIVE_DAY_REMOVAL|STAFFED_DAY_REMOVAL/.test(msg)) {
    return "The new dates would drop a day that was already started, closed or staffed. Keep that day in the dates.";
  }
  if (/end_time must be after start_time/.test(msg)) {
    return "The end date/time must be after the start.";
  }
  if ((e as { code?: string } | null)?.code === "55P03") {
    return "This event is busy (another change is being saved). Try again in a moment.";
  }
  return null;
}

/**
 * Soft delete an event (sets is_deleted = true)
 * Event remains in database but is hidden from UI and API
 */
export const softDeleteEvent = async (id: UUID) => {
  const { data, error } = await supabase
    .from("events")
    .update({
      is_deleted: true,
      deleted_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select()
    .single();
  if (error) {
    throw error;
  }
  return data;
};

/**
 * The status an operator-added attendee (invite / CSV import) is written with.
 *
 * Fixed 2026-10-03: both paths wrote attendance_status 'proposed' and no attendee_type, so every
 * insert failed (QA and PROD). The live CHECK allows only registered / confirmed / attended /
 * no_show / cancelled / attributed — there is NO 'proposed' or 'invited' state — and attendee_type
 * is NOT NULL with no default (member / prospect / guest / instructor / staff). 'registered' is the
 * column default and the only "expected, not yet here" value, so an operator-added person counts
 * toward capacity like any other registrant (and is refused by the hard cap when it is full).
 */
export const OPERATOR_ADDED_ATTENDANCE_STATUS = "registered" as const;

/**
 * attendee_type for an operator-added contact: 'member' when the contact carries membership
 * evidence (same cascade as the headcount split — membership_id wins), otherwise 'prospect',
 * which is what the public registration path writes for a non-member.
 */
export function operatorAddedAttendeeType(
  contact: MemberEvidenceLike | null | undefined,
): "member" | "prospect" {
  return contact && isMemberForHeadcount({ ...contact, attendee_type: null })
    ? "member"
    : "prospect";
}

/** The insert payload for an operator-added attendee (invite or CSV import). */
export const buildOperatorAddedAttendeeRow = async (
  eventId: UUID,
  contactId: UUID,
) => {
  const { data: contact } = await supabase
    .from("contacts")
    .select("membership_id, contact_type, has_active_subscription")
    .eq("id", contactId)
    .maybeSingle();
  return {
    event_id: eventId,
    contact_id: contactId,
    attendee_type: operatorAddedAttendeeType(contact),
    attendance_status: OPERATOR_ADDED_ATTENDANCE_STATUS,
  };
};

export const inviteAttendee = async (eventId: UUID, contactId: UUID) => {
  const row = await buildOperatorAddedAttendeeRow(eventId, contactId);
  const { data, error } = await supabase
    .from("event_attendees")
    .insert([row])
    .select()
    .single();
  if (error) {
    throw error;
  }
  return data;
};

export const updateAttendeeStatus = async (
  attendeeId: UUID,
  status: string,
) => {
  const { data, error } = await supabase
    .from("event_attendees")
    .update({
      attendance_status: status,
    })
    .eq("id", attendeeId)
    .select()
    .single();
  if (error) {
    throw error;
  }
  return data;
};

export const proposeAttendee = async (attendeeId: UUID) => {
  const { data, error } = await supabase
    .from("event_attendees")
    .update({
      attendance_status: "proposed",
    })
    .eq("id", attendeeId)
    .select()
    .single();
  if (error) {
    throw error;
  }
  return data;
};

export const getEventOrganizations = async (eventId: UUID) => {
  const { data, error } = await supabase
    .from("event_organizations")
    .select("*, organizations(*)")
    .eq("event_id", eventId);
  if (error) {
    throw error;
  }
  return data || [];
};

export const getEventLocations = async (eventId: UUID) => {
  const { data, error } = await supabase
    .from("event_locations")
    .select("*, locations(*, organizations(*))")
    .eq("event_id", eventId);
  if (error) {
    throw error;
  }
  return data || [];
};

export const getEventLinks = async (eventId: UUID) => {
  const { data, error } = await supabase
    .from("event_links")
    .select("*")
    .eq("event_id", eventId)
    .order("display_order", { ascending: true });
  if (error) {
    throw error;
  }
  return data || [];
};

export const getEventListAssociations = async (eventId: UUID) => {
  const { data, error } = await supabase
    .from("event_lists")
    .select("*, lists(*)")
    .eq("event_id", eventId);
  if (error) {
    throw error;
  }
  return data || [];
};

export const getEventAttendees = async (eventId: UUID) => {
  // FK-hinted: event_attendees now has TWO FKs to contacts (contact_id + invited_by_contact_id, added
  // in the guest-invite feature), so the plain `contacts(*)` embed became ambiguous (PGRST201). Hint
  // contact_id for the attendee's own contact, and embed the inviter (guest-invite) as `inviter`.
  const SELECT =
    "*, contacts!contact_id(*), inviter:contacts!invited_by_contact_id(first_name, last_name)";
  const PAGE = 1000;
  // KEYSET (seek) pagination, ordered (created_at DESC, id DESC). Each page seeks strictly PAST the
  // last row of the previous page instead of offsetting by a row count (re-audit-5 HIGH). On a LIVE
  // event, a concurrent insert shifts an offset window and causes a skipped OR duplicated attendee
  // (breaking card/tab parity); a keyset cursor is anchored to a specific row, so a concurrent insert
  // cannot move rows across a page boundary. PostgREST still caps one request at ~1000 rows, so we
  // page until a short page. The `+ id` tiebreak makes the seek total even when created_at ties.
  // Rows stay loosely typed (as the prior implementation returned) — the many downstream consumers
  // read embedded contacts/guest fields off them; only the keyset fields (created_at, id) are read
  // here for the cursor.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const all: any[] = [];
  let cursor: { created_at: string; id: string } | null = null;
  // Bound the loop far above any real attendee count as a runaway guard (10M rows).
  for (let guard = 0; guard < 10000; guard++) {
    let q = supabase
      .from("event_attendees")
      .select(SELECT)
      .eq("event_id", eventId)
      .order("created_at", { ascending: false })
      .order("id", { ascending: false })
      .limit(PAGE);
    if (cursor) {
      // (created_at, id) < (cursor.created_at, cursor.id) in the DESC ordering. Values come from a
      // DB row (ISO timestamp + uuid), never user input, and are percent-encoded by supabase-js v2.
      q = q.or(
        `created_at.lt.${cursor.created_at},and(created_at.eq.${cursor.created_at},id.lt.${cursor.id})`,
      );
    }
    const { data, error } = await q;
    if (error) {
      throw error;
    }
    const rows = data ?? [];
    all.push(...rows);
    if (rows.length < PAGE) {
      break;
    }
    const last = rows[rows.length - 1];
    cursor = { created_at: last.created_at, id: last.id };
  }
  return all;
};

/**
 * Pending guest invites for an event (item 2): minted invite tokens that are still `active` and not
 * yet confirmed — these have NO event_attendees row, so they don't appear in getEventAttendees. Reads
 * the get_event_pending_invites RPC (SECURITY DEFINER, authenticated).
 */
export interface EventPendingInvite {
  token_id: UUID;
  guest_contact_id: UUID | null;
  guest_first_name: string | null;
  guest_last_name: string | null;
  guest_email: string | null;
  invited_by_contact_id: UUID | null;
  inviter_name: string | null;
  created_at: string;
}

export const getEventPendingInvites = async (
  eventId: UUID,
): Promise<EventPendingInvite[]> => {
  const { data, error } = await supabase.rpc("get_event_pending_invites", {
    p_event_id: eventId,
  });
  if (error) {
    throw error;
  }
  return (data as EventPendingInvite[]) || [];
};

/**
 * Roster check-in pre-check (migration 1040). Asks the server to confirm, live with Zuora, a CRM non-member who
 * has a Zuora account -- BEFORE the roster button records the check-in, so the member award that fires on that
 * write can use the answer. Strictly best effort: bounded to 3.5 s on the client, and any failure resolves to
 * `{ verified: false }` so the roster check-in always proceeds exactly as before. Never writes anything itself.
 */
// Switch state, cached per tab for 5 minutes, so a roster check-in with the feature OFF costs at most one tiny read
// every 5 minutes and never an edge round trip (internal review H2).
let liveSwitchCache: { on: boolean; at: number } | null = null;
const LIVE_SWITCH_TTL_MS = 5 * 60 * 1000;

export function __resetLiveSwitchCacheForTests() {
  liveSwitchCache = null;
}

export async function preVerifyMemberLiveForRoster(
  attendeeId: string,
): Promise<{ verified: boolean }> {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), 3500);
  try {
    if (
      !liveSwitchCache ||
      Date.now() - liveSwitchCache.at > LIVE_SWITCH_TTL_MS
    ) {
      const { data: sw, error: swErr } = await supabase
        .from("system_config")
        .select("value")
        .eq("key", "checkin_zuora_live_enabled")
        .abortSignal(ctl.signal)
        .maybeSingle();
      liveSwitchCache = {
        on:
          !swErr &&
          String((sw as { value?: string } | null)?.value ?? "")
            .trim()
            .toLowerCase() === "true",
        at: Date.now(),
      };
    }
    if (!liveSwitchCache.on) {
      return { verified: false };
    }
    // Only a not-yet-checked-in attendee whose contact is a CRM non-member with its own Zuora account can benefit.
    const { data: att, error: attErr } = await supabase
      .from("event_attendees")
      .select(
        "checked_in_at, contacts(has_active_subscription, zuora_account_id)",
      )
      .eq("id", attendeeId)
      .abortSignal(ctl.signal)
      .maybeSingle();
    const a = att as {
      checked_in_at?: string | null;
      contacts?: {
        has_active_subscription?: boolean | null;
        zuora_account_id?: string | null;
      } | null;
    } | null;
    if (
      attErr ||
      !a ||
      a.checked_in_at ||
      !a.contacts ||
      a.contacts.has_active_subscription === true ||
      !String(a.contacts.zuora_account_id ?? "").trim()
    ) {
      return { verified: false };
    }
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) {
      return { verified: false };
    }
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/event-walkin-register`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ action: "live_verify", attendeeId }),
        signal: ctl.signal,
      },
    );
    const json = await res.json().catch(() => ({}));
    return { verified: res.ok && json?.verified === true };
  } catch {
    return { verified: false };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Mark a registered attendee present from the roster.
 *
 * ⚠️ Do NOT add `checkin_channel` or `checked_in_by` here. It looks like the obvious fix for these
 * check-ins reporting as "Unclassified" on Event Performance, and it silently does nothing:
 * `enforce_checkin_channel_write_once` nulls any channel written by a caller whose JWT role is not
 * `service_role`, and SECURITY DEFINER does not change that claim, so no RPC granted to
 * `authenticated` can set it either. The UPDATE's own RETURNING clause comes back with the channel
 * already stripped.
 *
 * Since migration 2026-09-26-003 the DATABASE derives both fields for this write — channel
 * `operator_roster`, actor `auth.uid()` — precisely because they cannot be trusted from a client.
 * `operator_roster` is a REPORTING receipt only and is deliberately absent from
 * RECEIPT_TO_TICKET_CHANNEL: this button runs none of the door checks the walk-in panel and booth
 * scanner do, so it must not authorise an invite-gate or capacity bypass the way `operator_walkin`
 * would.
 */
export const checkInAttendee = async (
  attendeeId: UUID,
  actual?: { memberCount?: number; nonMemberCount?: number },
) => {
  // Migration 1040: before recording the check-in, let the server confirm live with Zuora a CRM non-member who has
  // a Zuora account, so the member award that fires on this write can use it. Best effort and time-boxed (3.5 s);
  // its answer never changes what this function writes, and any failure is ignored.
  await preVerifyMemberLiveForRoster(attendeeId).catch(() => undefined);
  const update: Record<string, unknown> = {
    attendance_status: "attended",
    checked_in_at: new Date().toISOString(),
  };
  // Verified headcount entered at the event (registered vs actual). Only written
  // when staff supply it; otherwise the attended_* columns stay NULL.
  if (actual && typeof actual.memberCount === "number") {
    update.attended_member_count = actual.memberCount;
  }
  if (actual && typeof actual.nonMemberCount === "number") {
    update.attended_nonmember_count = actual.nonMemberCount;
  }
  const { data, error } = await supabase
    .from("event_attendees")
    .update(update)
    .eq("id", attendeeId)
    .select()
    .single();
  if (error) {
    throw error;
  }
  return data;
};

export const getEventVouchers = async (eventId: UUID) => {
  // Returns BOTH event-linked vouchers AND event-ticket reward vouchers (minted with event_id = NULL,
  // linked via vouchers.token_id -> redemption_tokens.event_id). A plain .eq("event_id", ...) missed
  // ticket rewards + their redemptions on the event Vouchers tab. See migration 2026-07-02-007.
  const { data, error } = await supabase.rpc("get_event_vouchers", {
    p_event_id: eventId,
  });
  if (error) {
    throw error;
  }
  // Exclude $0 non-monetary reward vouchers (e.g. Event Drawing entries) from the event's voucher list +
  // metrics — they're tracked on the Drawing tab, not counted as vouchers. Positive-value ticket rewards
  // still appear.
  return (
    (
      data as Array<{
        is_non_monetary?: boolean | null;
        token_id?: string | null;
        applied_amount?: number | null;
        discount_value?: number | null;
      }> | null
    )?.filter(
      (v) =>
        !v.is_non_monetary &&
        // $0 ticket placeholders (token-linked, no real value) are admissions, not vouchers
        // (audit iteration 1) — matches the comment above: positive-value ticket rewards appear.
        !(v.token_id && Number(v.applied_amount ?? v.discount_value ?? 0) <= 0),
    ) || []
  );
};

export const promoteFromWaitlist = async (attendeeId: UUID) => {
  // The DB CHECK constraint allows only registered/confirmed/attended/no_show/
  // cancelled/attributed. Promoting a waitlisted attendee makes them a valid advance
  // registration ('accepted' was invalid and would violate the constraint).
  const { data, error } = await supabase
    .from("event_attendees")
    .update({ attendance_status: "registered" })
    .eq("id", attendeeId)
    .select()
    .single();
  if (error) {
    throw error;
  }
  return data;
};

// Comment management
export interface EventComment {
  id: UUID;
  event_id: UUID;
  user_id: UUID;
  comment: string;
  created_at: string;
  updated_at?: string;
}

export const getEventComments = async (
  eventId: UUID,
): Promise<EventComment[]> => {
  const { data, error } = await supabase
    .from("event_comments")
    .select("*")
    .eq("event_id", eventId)
    .order("created_at", { ascending: false });
  if (error) {
    throw error;
  }
  return data || [];
};

export const createEventComment = async (
  eventId: UUID,
  userId: UUID,
  comment: string,
): Promise<EventComment> => {
  const { data, error } = await supabase
    .from("event_comments")
    .insert({
      event_id: eventId,
      created_by: userId,
      comment_text: comment,
    })
    .select()
    .single();
  if (error) {
    console.error("Error creating comment:", error);
    throw error;
  }
  return data;
};

export const updateEventComment = async (
  commentId: UUID,
  comment: string,
): Promise<EventComment> => {
  const { data, error } = await supabase
    .from("event_comments")
    .update({
      comment_text: comment,
      updated_at: new Date().toISOString(),
      is_edited: true,
    })
    .eq("id", commentId)
    .select()
    .single();
  if (error) {
    throw error;
  }
  return data;
};

export const deleteEventComment = async (commentId: UUID): Promise<void> => {
  const { error } = await supabase
    .from("event_comments")
    .delete()
    .eq("id", commentId);
  if (error) {
    throw error;
  }
};

export const deleteEvent = async (id: UUID): Promise<void> => {
  const { error } = await supabase.from("events").delete().eq("id", id);
  if (error) {
    throw error;
  }
};

export const saveEventOrgLocations = async (
  eventId: UUID,
  selections: Array<{ org_id: UUID; location_id: UUID | null }>,
): Promise<void> => {
  // Atomic replace via the set_event_org_associations SECURITY DEFINER RPC.
  //
  // This previously did a CLIENT-SIDE clear-and-rewrite: DELETE event_organizations,
  // DELETE event_locations, then N one-by-one INSERTs — with NO transaction. Any
  // mid-sequence failure (an RLS refusal, a constraint violation, a dropped
  // connection) left the associations partial or empty and unrecoverable (external
  // audit BLOCKER — total/partial data loss). The RPC does the whole delete + reinsert
  // of BOTH junction tables in ONE transaction, so a failure rolls the entire operation
  // back. It self-authorizes with has_event_permission(event, uid, 'edit') — the same
  // event-edit authorization the events UPDATE RLS uses — and returns the new set.
  //
  // Only org_id / location_id are sent (selection objects may carry display-only
  // org_name / location_name that the RPC ignores).
  const { error } = await supabase.rpc("set_event_org_associations", {
    p_event_id: eventId,
    p_selections: selections.map((selection) => ({
      org_id: selection.org_id,
      location_id: selection.location_id ?? null,
    })),
  });
  if (error) {
    throw error;
  }
};

export const saveEventLinks = async (
  eventId: UUID,
  links: Array<{
    id?: UUID;
    url: string;
    label: string;
    description?: string;
    link_type?: string;
    display_order?: number;
  }>,
): Promise<void> => {
  // Delete existing links
  await supabase.from("event_links").delete().eq("event_id", eventId);

  // Insert new links
  if (links.length > 0) {
    const { error } = await supabase.from("event_links").insert(
      links.map((link, index) => ({
        event_id: eventId,
        url: link.url,
        label: link.label,
        description: link.description,
        link_type: link.link_type,
        display_order: link.display_order ?? index,
      })),
    );
    if (error) {
      throw error;
    }
  }
};

export const saveEventListAssociations = async (
  eventId: UUID,
  listSelections: Array<{ list_id: UUID }>,
): Promise<void> => {
  // Replace the hand-picked associations. Rows linked by an Event in a Box launch (eib_launch_id, migration
  // 894) are NOT part of the form's selection: leave them alone (the database also skips a client delete of
  // them, which protects older open tabs that still delete every row).
  const { error: deleteError } = await supabase
    .from("event_lists")
    .delete()
    .eq("event_id", eventId)
    .is("eib_launch_id", null);
  if (deleteError) {
    throw deleteError;
  }

  // Insert new associations
  if (listSelections.length > 0) {
    const { error } = await supabase.from("event_lists").insert(
      listSelections.map((selection) => ({
        event_id: eventId,
        list_id: selection.list_id,
      })),
    );
    if (error) {
      throw error;
    }
  }
};

/**
 * Duplicate an event
 * Creates a copy of an event with a new title as a scheduled event
 */
/**
 * Multi-date variant of duplicateEvent. Clones the source event into
 * each of the supplied dates atomically via the duplicate_event RPC.
 * Returns the new event ids in the same order as `dates`.
 * Max 24 dates per call (RPC-enforced).
 */
export interface DuplicateOutcome {
  /** The new events, in the order duplicate_event returned them (for dates: the order of `dates`). */
  ids: string[];
  /** Duplicates that exist but did not get the original's Sales Reps (date-model events only; empty otherwise). */
  staffingCopyFailures: StaffingCopyFailure[];
}

export const duplicateEventToDates = async (
  eventId: UUID,
  dates: string[], // ISO YYYY-MM-DD strings
  ctaIds?: string[],
): Promise<DuplicateOutcome> => {
  const { data, error } = await supabase.rpc("duplicate_event", {
    p_source_event_id: eventId,
    p_dates: dates,
    p_cta_ids: ctaIds ?? null,
    p_options: {},
  });
  if (error) {
    throw error;
  }
  const ids = (data as string[]) ?? [];
  // duplicate_event has committed: from here on nothing may turn this into a failed duplication.
  const { failed } = await copyStaffingToDuplicates(eventId, ids);
  return { ids, staffingCopyFailures: failed };
};

export const duplicateEvent = async (
  eventId: UUID,
  ctaIds?: string[],
): Promise<DuplicateOutcome & { event: Event }> => {
  // 2026-06-06 (Sprint A Batch 2 Ship C): redirected to the
  // duplicate_event RPC so single-event and multi-date clones share
  // one atomic, permission-checked, schema-drift-resilient code path.
  // Audit prompt: docs/AUDIT_PROMPT_SMART_ADMIN_SPRINT_A_BATCH_2.md
  // (R3 GREEN).
  const { data: newIds, error: rpcError } = await supabase.rpc(
    "duplicate_event",
    {
      p_source_event_id: eventId,
      p_dates: null,
      p_cta_ids: ctaIds ?? null,
      p_options: {},
    },
  );
  if (rpcError) {
    throw rpcError;
  }
  const ids = newIds as string[] | null;
  if (!ids || ids.length === 0) {
    throw new Error("duplicate_event returned no ids");
  }
  // duplicate_event has committed: the staffing copy reports, it never throws.
  const { failed } = await copyStaffingToDuplicates(eventId, ids);
  const { data: created, error: fetchNewError } = await supabase
    .from("events")
    .select("*")
    .eq("id", ids[0])
    .single();
  if (fetchNewError) {
    throw fetchNewError;
  }
  return {
    event: created as Event,
    ids,
    staffingCopyFailures: failed,
  };
};

/**
 * Bulk update event status
 */
export const bulkUpdateEventStatus = async (
  eventIds: UUID[],
  status: string,
): Promise<void> => {
  const { error } = await supabase
    .from("events")
    .update({ status })
    .in("id", eventIds);

  if (error) {
    throw error;
  }
};

/** Outcome of a bulk delete: what went, what stayed, and WHY it stayed. */
export interface BulkDeleteResult {
  deleted: UUID[];
  failed: { id: UUID; reason: string }[];
}

/**
 * Bulk delete events — a SOFT delete, matching what the row-level Delete action does.
 *
 * Two bugs were fixed here. It used to issue a hard `DELETE ... IN (...)`, which
 *   (a) disagreed with the row action: clicking Delete on one row soft-deletes, so selecting 19
 *       rows and clicking Delete Selected should not do something categorically different, and
 *   (b) was atomic, so ONE undeletable event took the whole selection down and the user was told
 *       only "Failed to delete events".
 * The hard delete also tripped `prevent_last_owner_removal` during its cascade into
 * event_permissions, which made it fail for essentially every event (fixed separately in
 * migration 2026-09-04-006 so the hard path is not left as a landmine).
 *
 * Rows are updated one at a time so a single failure cannot hide the successes, and
 * `.select("id")` is required: an event blocked by RLS updates zero rows and would otherwise
 * report success and quietly reappear on the next refresh.
 */
export const bulkDeleteEvents = async (
  eventIds: UUID[],
): Promise<BulkDeleteResult> => {
  const result: BulkDeleteResult = { deleted: [], failed: [] };

  for (const id of eventIds) {
    const { data, error } = await supabase
      .from("events")
      .update({ is_deleted: true, deleted_at: new Date().toISOString() })
      .eq("id", id)
      .select("id");

    if (error) {
      result.failed.push({ id, reason: describeDeleteError(error) });
    } else if (!data || data.length === 0) {
      result.failed.push({
        id,
        reason: "you do not have permission to delete this event",
      });
    } else {
      result.deleted.push(id);
    }
  }

  return result;
};

/** Turn a Postgres error into something an operator can act on. */
export const describeDeleteError = (error: {
  message?: string;
  code?: string;
  details?: string;
}): string => {
  const raw = `${error?.message ?? ""} ${error?.details ?? ""}`;

  // Migrations 2026-10-08-401/403/405 (MSR plan round 9, D20): an event with activity (check-ins, verification,
  // rewards, vouchers/tickets, drawings, referrals, sent invitations/reminders) cannot be deleted; cancel it instead.
  if (/EVENT_HAS_ACTIVITY/.test(raw)) {
    return "it already has activity (check-ins, verified sign-ups, rewards, vouchers or sent invitations), so it can't be deleted — cancel it instead";
  }
  if (/last owner/i.test(raw)) {
    return "blocked by the event's owner permissions";
  }
  // 23503 = foreign_key_violation. Name the record that is holding the event, because the fix is
  // to deal with that record, not to retry the delete.
  if (error?.code === "23503" || /violates foreign key/i.test(raw)) {
    if (/offers/i.test(raw)) {
      return "an offer is attached to it";
    }
    if (/referral_credits/i.test(raw)) {
      return "referral credits are attached to it";
    }
    if (/organization_conversions/i.test(raw)) {
      return "an organization conversion is attributed to it";
    }
    if (/sms_consent/i.test(raw)) {
      return "SMS consent records reference it (these are kept on purpose)";
    }
    return "another record still references it";
  }
  return error?.message || "unknown error";
};

/**
 * Event funnel: how many were emailed an invitation, how many registered, how many turned up.
 *
 * Rewritten 2026-09-06. The previous implementation counted `event_attendees.attendance_status`
 * itself and got two of its three numbers wrong, on every event:
 *   - it counted 'proposed' as "invited". That value is FORBIDDEN by the attendance_status CHECK
 *     (registered/confirmed/attended/no_show/cancelled/attributed), so it could never be anything
 *     but zero. 'invited' was equally impossible.
 *   - it counted 'checked_in' as "attended". The stored value is 'attended'. PROD holds 927 attended
 *     rows across 29 events and the funnel reported 0 for all of them.
 * It also diverged from the Attendees tab, which already reads the canonical, audit-hardened
 * get_event_attendance_summary — so the same page showed two different answers.
 *
 * Now: attendance comes from that one canonical RPC, and "invited" means what was ACTUALLY SENT
 * (campaign_flights.emails_sent on the event's campaigns), which is the only honest source for an
 * EIB event — invitations are emailed off campaign flights and never written to event_attendees.
 *
 * Fixed again 2026-09-09. `attended` returned the resolver's `attended_headcount` verbatim. That
 * field is filtered to cohort='checked_in' ONLY — walk-ins are a separate cohort and are excluded
 * from it BY DESIGN — so the funnel printed "16 Attended" for PROD event 51981c06 while 23 people
 * were actually there, and disagreed with the Attendees tab on the same page. The field name and
 * the label meant different things. `attended` is now the canonical total (advance check-ins PLUS
 * walk-ins) and the walk-in half is returned separately so the UI can label it instead of hiding it.
 */
export const getEventFunnelStats = async (
  eventId: UUID,
): Promise<{
  /**
   * UNIQUE PEOPLE invited. Never a message count.
   *
   * Was: the sum of campaign_flights.emails_sent across the event's sent flights. One person who
   * got an invite and two reminders counted as three "Invited", and the funnel then divided
   * REGISTERED PEOPLE by that MESSAGE count to produce a conversion rate. The three message
   * figures below are reported separately and are never rendered as people.
   */
  invited: number;
  /** Messages from the initial invitation flights (eib_flight_role='invite'). */
  initialInviteMessages: number;
  /** Messages from reminder flights (eib_flight_role='invite_reminder'). */
  reminderMessages: number;
  /** Every message on the event's campaigns, whatever the flight's role. */
  totalCampaignMessages: number;
  registered: number;
  /** Bodies present, everyone: advance check-ins + walk-ins. */
  attended: number;
  /** Of `attended`, those who had registered in advance. */
  attendedAdvance: number;
  /** Of `attended`, those who walked in — a labelled subset, never hidden. */
  attendedWalkIn: number;
  /** Advance-registered bodies (the honest denominator for a show rate). */
  registeredHeadcount: number;
  /** attendedAdvance / registeredHeadcount, straight from the resolver. */
  advanceShowRate: number | null;
  cancelled: number;
}> => {
  const [summaryRes, inviteRes] = await Promise.all([
    supabase.rpc("get_event_attendance_summary", { p_event_id: eventId }),
    // ONE canonical invite contract, shared with Event Performance. Computing this in the browser
    // is what produced the people/messages confusion; the rollup is now the only definition.
    supabase.rpc("get_event_invite_summary", { p_event_id: eventId }),
  ]);

  if (summaryRes.error) {
    throw summaryRes.error;
  }
  const summary = Array.isArray(summaryRes.data)
    ? summaryRes.data[0]
    : summaryRes.data;

  // A read failure must NOT be reported as "nobody was invited" — a real zero and a failed read
  // look identical to the reader. The RPC returns no row when the event has no campaigns and no
  // invite tokens, which is a genuine zero; an actual error is rethrown.
  if (inviteRes.error) {
    throw inviteRes.error;
  }
  const inviteRow = (
    Array.isArray(inviteRes.data) ? inviteRes.data[0] : inviteRes.data
  ) as
    | {
        invited_people_count: number | null;
        initial_invite_messages: number | null;
        reminder_messages: number | null;
        total_campaign_messages: number | null;
      }
    | null
    | undefined;
  const invited = inviteRow?.invited_people_count ?? 0;
  const initialInviteMessages = inviteRow?.initial_invite_messages ?? 0;
  const reminderMessages = inviteRow?.reminder_messages ?? 0;
  const totalCampaignMessages = inviteRow?.total_campaign_messages ?? 0;

  // ONE canonical attendance contract, shared with the Attendees tab (utils/eventAttendance).
  const canonical = canonicalAttendance({
    registered_rows: summary?.registered_rows ?? 0,
    registered_headcount: summary?.registered_headcount ?? 0,
    checked_in_rows: summary?.checked_in_rows ?? 0,
    attended_headcount: summary?.attended_headcount ?? 0,
    walk_in_rows: summary?.walk_in_rows ?? 0,
    walk_in_headcount: summary?.walk_in_headcount ?? 0,
    attendance_rate: summary?.attendance_rate ?? null,
  });

  return {
    invited,
    initialInviteMessages,
    reminderMessages,
    totalCampaignMessages,
    registered: canonical.registeredRows,
    attended: canonical.attendedTotal,
    attendedAdvance: canonical.attendedAdvance,
    attendedWalkIn: canonical.attendedWalkIn,
    registeredHeadcount: canonical.registeredHeadcount,
    advanceShowRate: canonical.advanceShowRate,
    // No 'waitlisted' here, deliberately. There is NO waitlist in this system:
    // event_attendees.attendance_status permits only registered/confirmed/attended/no_show/
    // cancelled/attributed. The funnel used to count the impossible value 'waitlisted', so it was
    // permanently 0 and the row never rendered. On 2026-09-06 I repointed it at
    // get_event_attendance_summary.awaiting_rows, which counts cohort='awaiting_checkin' — people who
    // registered and have not checked in YET. That is a SUBSET of registered, not a separate cohort,
    // so a scheduled event showed the same people twice ("40 Registered / 40 Waitlisted") and an
    // operator reasonably asked what the 40 waitlisted were. Nothing was waitlisted; they were
    // registered for an event that had not happened yet.
    cancelled: summary?.cancelled_rows ?? 0,
  };
};

/**
 * Look up attendee by check-in code
 */
export const getAttendeeByCheckinCode = async (
  code: string,
): Promise<{
  attendee_id: string;
  event_id: string;
  event_title: string;
  event_start_time: string;
  attendee_name: string;
  attendee_email: string;
  attendance_status: string;
  is_checked_in: boolean;
  check_in_time: string | null;
  on_waitlist: boolean;
} | null> => {
  const { data, error } = await supabase.rpc("get_attendee_by_checkin_code", {
    p_code: code.toUpperCase(),
  });

  if (error) {
    console.error("Error looking up check-in code:", error);
    throw error;
  }

  return data && data.length > 0 ? data[0] : null;
};

/**
 * Check in an attendee by their check-in code
 */
export const checkinAttendeeByCode = async (
  code: string,
  checkedInBy?: string,
  location?: string,
): Promise<{
  success: boolean;
  message: string;
  attendee_id: string | null;
  attendee_name: string | null;
  event_title: string | null;
  check_in_time: string | null;
}> => {
  const { data, error } = await supabase.rpc("checkin_attendee_by_code", {
    p_code: code.toUpperCase(),
    p_checked_in_by: checkedInBy || null,
    p_location: location || null,
    p_checkin_channel: "code_scan",
  });

  if (error) {
    console.error("Error checking in attendee:", error);
    throw error;
  }

  return data && data.length > 0
    ? data[0]
    : {
        success: false,
        message: "Check-in failed",
        attendee_id: null,
        attendee_name: null,
        event_title: null,
        check_in_time: null,
      };
};

/**
 * Generate check-in code for an attendee (if not already generated)
 */
export const generateCheckinCodeForAttendee = async (
  attendeeId: string,
): Promise<string> => {
  // First check if code already exists
  const { data: existing } = await supabase
    .from("event_attendees")
    .select("checkin_code")
    .eq("id", attendeeId)
    .single();

  if (existing?.checkin_code) {
    return existing.checkin_code;
  }

  // Generate new code using the database function
  const { data, error } = await supabase.rpc("generate_checkin_code");

  if (error) {
    console.error("Error generating check-in code:", error);
    throw error;
  }

  // Update the attendee with the new code
  const { error: updateError } = await supabase
    .from("event_attendees")
    .update({
      checkin_code: data,
      checkin_code_generated_at: new Date().toISOString(),
    })
    .eq("id", attendeeId);

  if (updateError) {
    console.error("Error saving check-in code:", updateError);
    throw updateError;
  }

  return data;
};

/**
 * Get check-in URL for an attendee
 */
export const getCheckinUrl = (checkinCode: string): string => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/checkin?code=${checkinCode}`;
};

// =====================================================
// TICKET TIERS
// =====================================================

import type {
  EventTicketTier,
  CreateTicketTierInput,
  UpdateTicketTierInput,
} from "../types/event.types";

/**
 * Get ticket tiers for an event
 */
export const getEventTicketTiers = async (
  eventId: UUID,
): Promise<EventTicketTier[]> => {
  const { data, error } = await supabase
    .from("event_ticket_tiers")
    .select("*")
    .eq("event_id", eventId)
    .order("sort_order", { ascending: true });

  if (error) {
    console.error("Error fetching ticket tiers:", error);
    throw error;
  }

  // Get tickets sold count for each tier
  const tiers = await Promise.all(
    (data || []).map(async (tier) => {
      const { count } = await supabase
        .from("event_attendees")
        .select("*", { count: "exact", head: true })
        .eq("ticket_tier_id", tier.id)
        .in("attendance_status", ["registered", "attended"]);

      return {
        ...tier,
        tickets_sold: count || 0,
        tickets_available: tier.capacity
          ? tier.capacity - (count || 0)
          : undefined,
      };
    }),
  );

  return tiers;
};

/**
 * Create a ticket tier
 */
export const createTicketTier = async (
  input: CreateTicketTierInput,
): Promise<EventTicketTier> => {
  const { data, error } = await supabase
    .from("event_ticket_tiers")
    .insert({
      ...input,
      price: input.price || 0,
      sort_order: input.sort_order || 0,
      is_active: input.is_active !== false,
    })
    .select()
    .single();

  if (error) {
    console.error("Error creating ticket tier:", error);
    throw error;
  }

  return data;
};

/**
 * Update a ticket tier
 */
export const updateTicketTier = async (
  tierId: UUID,
  input: UpdateTicketTierInput,
): Promise<EventTicketTier> => {
  const { data, error } = await supabase
    .from("event_ticket_tiers")
    .update({
      ...input,
      updated_at: new Date().toISOString(),
    })
    .eq("id", tierId)
    .select()
    .single();

  if (error) {
    console.error("Error updating ticket tier:", error);
    throw error;
  }

  return data;
};

/**
 * Delete a ticket tier
 */
export const deleteTicketTier = async (tierId: UUID): Promise<void> => {
  const { error } = await supabase
    .from("event_ticket_tiers")
    .delete()
    .eq("id", tierId);

  if (error) {
    console.error("Error deleting ticket tier:", error);
    throw error;
  }
};

/**
 * Get event capacity stats
 */
export const getEventCapacityStats = async (eventId: UUID) => {
  // Get event capacity
  const { data: event, error: eventError } = await supabase
    .from("events")
    .select("capacity, waitlist_capacity")
    .eq("id", eventId)
    .single();

  if (eventError) {
    throw eventError;
  }

  // Get attendee counts
  const { count: registeredCount } = await supabase
    .from("event_attendees")
    .select("*", { count: "exact", head: true })
    .eq("event_id", eventId)
    .in("attendance_status", ["registered", "attended"]);

  const { count: waitlistCount } = await supabase
    .from("event_attendees")
    .select("*", { count: "exact", head: true })
    .eq("event_id", eventId)
    .eq("attendance_status", "waitlisted");

  return {
    capacity: event?.capacity || null,
    waitlist_capacity: event?.waitlist_capacity || 0,
    registered: registeredCount || 0,
    waitlisted: waitlistCount || 0,
    available: event?.capacity ? event.capacity - (registeredCount || 0) : null,
    waitlist_available: event?.waitlist_capacity
      ? event.waitlist_capacity - (waitlistCount || 0)
      : 0,
  };
};

const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// Recipients for the incomplete-event heads-up. send-email takes ONE recipient,
// so we send one email per address. The temporary second recipient (added 2026-08-19) was removed
// by the owner on 2026-10-02. Keep this list equal to system_config 'events_inbox_recipients'
// (the cancelled-event notice); test_event_cancelled_inbox_notice.py R1 checks they match.
const INCOMPLETE_EVENT_NOTIFY_RECIPIENTS = ["events@uslawshield.com"];

// Only these event types warrant a heads-up to the events mailbox (owner, 2026-09-09). Everything
// else -- classes, seminars, speaking engagements, tables, conferences and the legacy `show` type --
// is created and configured without that team's involvement, so notifying them was noise.
//
// The gate lives HERE rather than at the call site on purpose: notify is currently wired only into
// EventCreateWizard, and a second caller (an EIB-direct path is the obvious candidate) would
// otherwise reintroduce the noise while looking correct.
const INCOMPLETE_EVENT_NOTIFY_TYPES = new Set(["gun_show", "range_night"]);

/**
 * Best-effort: email the events team (events@uslawshield.com) that a new event
 * was saved but not yet configured, so they can finish setting it up. Uses the
 * shared send-email edge function (type 'notification' — arbitrary recipient,
 * plain subject/body, no template). Fire-and-forget: NEVER throws and callers
 * must not block their save/navigate on it; the events list badges incomplete
 * events as a backstop. Returns true on a clean send, false on any failure.
 */
export const notifyEventNeedsConfiguration = async (event: {
  id: UUID;
  title: string;
  type?: string | null;
  startTime?: string | null;
  /** Absolute link to the event page, e.g. `${window.location.origin}/events/<id>`. */
  url: string;
  /** mig 1241: an RM/TM-created event of any type waits for its QR code to be published by the events team. */
  qrPending?: boolean;
}): Promise<boolean> => {
  // Types are stored lower_snake_case; normalise anyway so a stray case or space cannot silently
  // suppress a notification that should have gone out.
  const eventType = (event.type ?? "").trim().toLowerCase();
  // Owner 2026-10-29: no event CTA / QR for class, table, seminar, speaking engagement: never ask for a QR.
  const qrPending = event.qrPending === true && !eventTypeHasNoCtaQr(eventType);
  if (!INCOMPLETE_EVENT_NOTIFY_TYPES.has(eventType) && !qrPending) {
    return false;
  }

  try {
    const title = (event.title || "Untitled event").trim();
    const typeLabel = event.type ? ` (${event.type})` : "";
    const startLabel = event.startTime
      ? new Date(event.startTime).toLocaleString()
      : null;

    const subject = `Event needs configuration: ${title}`;
    const qrNote = qrPending
      ? "It was created by a Regional or Territory Manager, so its call-to-action QR code still needs to be published (open its Calls to Action tab). "
      : "";
    const textBody =
      "A new event was created but has not been fully configured yet, so it " +
      "will not send invitations or issue tickets until setup is finished.\n\n" +
      qrNote +
      `Event: ${title}${typeLabel}` +
      (startLabel ? `\nStart: ${startLabel}` : "") +
      `\n\nFinish configuring it here:\n${event.url}`;
    const htmlBody =
      "<p>A new event was created but has not been fully configured yet, so it " +
      "will not send invitations or issue tickets until setup is finished.</p>" +
      (qrNote ? `<p>${escapeHtml(qrNote)}</p>` : "") +
      `<p><strong>${escapeHtml(title)}</strong>${escapeHtml(typeLabel)}` +
      (startLabel ? `<br/>Start: ${escapeHtml(startLabel)}` : "") +
      "</p>" +
      `<p><a href="${encodeURI(event.url)}">Finish configuring this event</a></p>`;

    const results = await Promise.all(
      INCOMPLETE_EVENT_NOTIFY_RECIPIENTS.map((to) =>
        supabase.functions
          .invoke("send-email", {
            body: {
              type: "notification",
              to,
              subject,
              textBody,
              htmlBody,
              caller_function: "incomplete-event-notify",
            },
          })
          .then(({ error }) => {
            if (error) {
              console.warn(
                `notifyEventNeedsConfiguration: send-email to ${to} failed`,
                error,
              );
              return false;
            }
            return true;
          })
          .catch((err) => {
            console.warn(
              `notifyEventNeedsConfiguration: send-email to ${to} threw`,
              err,
            );
            return false;
          }),
      ),
    );
    // True only if EVERY recipient was sent cleanly; never throws either way.
    return results.every(Boolean);
  } catch (err) {
    console.warn("notifyEventNeedsConfiguration: unexpected error", err);
    return false;
  }
};

/**
 * Promo code per event for the Events list (migration 2026-09-29-023): the code of the organization the
 * event's CTA was prepared from (promoter for gun shows, host for range nights), else the host's. Staff
 * only server-side; returns {} for anyone else or on error, so the column simply stays blank.
 */
/**
 * Which of these events the signed-in user can change (migration 2026-10-01-001). Every Events-list
 * action (Cancel, Delete -- a soft delete -- and the bulk actions) is an UPDATE, evaluated server-side
 * with the same predicates as the events UPDATE policies, so the list only offers actions that will
 * actually work. Fails CLOSED: on error no event is treated as changeable.
 */
export const getEventModifyRights = async (
  eventIds: string[],
): Promise<Record<string, boolean>> => {
  if (eventIds.length === 0) {
    return {};
  }
  const { data, error } = await supabase.rpc("event_modify_rights", {
    p_event_ids: eventIds.slice(0, 500),
  });
  if (error || !data) {
    return {};
  }
  const out: Record<string, boolean> = {};
  for (const row of data as Array<{ event_id: string; can_update: boolean }>) {
    out[row.event_id] = row.can_update === true;
  }
  return out;
};

export const getEventPromoCodes = async (
  eventIds: string[],
): Promise<Record<string, string>> => {
  if (eventIds.length === 0) {
    return {};
  }
  const { data, error } = await supabase.rpc("get_event_promo_codes", {
    p_event_ids: eventIds.slice(0, 500),
  });
  if (error || !data) {
    return {};
  }
  const out: Record<string, string> = {};
  for (const row of data as Array<{
    event_id: string;
    promo_code: string | null;
  }>) {
    if (row.promo_code) {
      out[row.event_id] = row.promo_code;
    }
  }
  return out;
};

/** get_event_promo_codes refuses (returns nothing) above this many ids per call. */
export const EVENT_PROMO_CODES_CHUNK_SIZE = 500;
const EVENT_PROMO_CODES_CONCURRENCY = 4;

/**
 * Strict variant for the Events export (2026-10-26). Unlike getEventPromoCodes (the list's
 * best-effort lookup, which swallows errors and only covers the first 500 ids), this one looks
 * up EVERY id in chunks of <= 500 and THROWS on any failed chunk, so an export can never ship
 * a silently blank Promo Code column.
 */
export const getEventPromoCodesStrict = async (
  eventIds: string[],
): Promise<Record<string, string>> => {
  const ids = Array.from(new Set(eventIds.filter(Boolean)));
  if (ids.length === 0) {
    return {};
  }
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += EVENT_PROMO_CODES_CHUNK_SIZE) {
    chunks.push(ids.slice(i, i + EVENT_PROMO_CODES_CHUNK_SIZE));
  }
  const fetchChunk = async (chunk: string[]) => {
    const { data, error } = await supabase.rpc("get_event_promo_codes", {
      p_event_ids: chunk,
    });
    if (error) {
      throw toRpcError("Could not load event promo codes", error);
    }
    if (!Array.isArray(data)) {
      throw new Error(
        "Could not load event promo codes: the server returned no result",
      );
    }
    return data as Array<{ event_id: string; promo_code: string | null }>;
  };
  // At most EVENT_PROMO_CODES_CONCURRENCY calls in flight (a 50k-row export is 100 chunks).
  const results: Array<Array<{ event_id: string; promo_code: string | null }>> =
    [];
  for (let i = 0; i < chunks.length; i += EVENT_PROMO_CODES_CONCURRENCY) {
    results.push(
      ...(await Promise.all(
        chunks.slice(i, i + EVENT_PROMO_CODES_CONCURRENCY).map(fetchChunk),
      )),
    );
  }
  const out: Record<string, string> = {};
  for (const rows of results) {
    for (const row of rows) {
      if (row.promo_code) {
        out[row.event_id] = row.promo_code;
      }
    }
  }
  return out;
};

/** The schedule columns get_events_for_organizations does not return (GS-04). */
export interface EventScheduleFields {
  id: UUID;
  timezone: string | null;
  date_tbd: boolean | null;
  all_day: boolean | null;
  start_date: string | null;
  end_date: string | null;
}

/**
 * GS-04: the date-filtered events query (RPC get_events_for_organizations) returns no `timezone`/`date_tbd`, so the
 * Events calendar placed every event on the VIEWER's day while the list a day chip opens matches days in the EVENT's
 * time zone: a gun show showed on a day whose list was empty ("shows but cannot be opened"). This reads the missing
 * columns for the rows already returned (same events RLS as the list). Throws on error: the caller decides.
 */
export async function getEventScheduleFields(
  ids: UUID[],
): Promise<Map<string, EventScheduleFields>> {
  const unique = [...new Set(ids)].filter(Boolean);
  const out = new Map<string, EventScheduleFields>();
  for (let i = 0; i < unique.length; i += 150) {
    const { data, error } = await supabase
      .from("events")
      .select("id, timezone, date_tbd, all_day, start_date, end_date")
      .in("id", unique.slice(i, i + 150));
    if (error) {
      throw error;
    }
    for (const r of (data ?? []) as EventScheduleFields[]) {
      out.set(r.id, r);
    }
  }
  return out;
}
